import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Joystick.css';

const Joystick = ({ onMove, onStop, isDisabled = false, isInfoMode = false, onClick = () => { } }) => {
    const containerRef = useRef(null);
    const knobRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const positionRef = useRef({ x: 0, y: 0 }); // Use ref to avoid closure issues
    const [activeTouchId, setActiveTouchId] = useState(null);
    const infoBtnRef = useRef(null);

    // Calculate knob position relative to container center
    const calculateKnobPosition = useCallback((clientX, clientY) => {
        const container = containerRef.current;
        if (!container || !knobRef.current) return { x: 0, y: 0 };

        const rect = container.getBoundingClientRect();
        const containerCenterX = rect.width / 2;
        const containerCenterY = rect.height / 2;

        let x = clientX - rect.left - containerCenterX;
        let y = clientY - rect.top - containerCenterY;

        // Calculate distance from center
        const distance = Math.sqrt(x * x + y * y);
        const maxDistance = containerCenterX - (knobRef.current.offsetWidth / 2);

        // If knob is dragged outside container radius, constrain it
        if (distance > maxDistance) {
            const angle = Math.atan2(y, x);
            x = Math.cos(angle) * maxDistance;
            y = Math.sin(angle) * maxDistance;
        }

        return { x, y };
    }, [containerRef, knobRef]);

    // Normalize position to values between -1 and 1
    const normalizePosition = useCallback((pos) => {
        const container = containerRef.current;
        const knob = knobRef.current;
        if (!container || !knob) return { x: 0, y: 0 };

        const maxDistance = container.offsetWidth / 2 - knob.offsetWidth / 2;
        if (maxDistance === 0) return { x: 0, y: 0 }; // Avoid division by zero

        return {
            x: pos.x / maxDistance,
            y: pos.y / maxDistance
        };
    }, [containerRef, knobRef]);

    const handleStart = useCallback((e) => {
        e.preventDefault();

        const touch = e.touches ? e.touches[0] : null;
        const clientX = touch ? touch.clientX : e.clientX;
        const clientY = touch ? touch.clientY : e.clientY;

        setIsDragging(true);
        setActiveTouchId(touch ? touch.identifier : null); // Save the touch ID

        const newPos = calculateKnobPosition(clientX, clientY);
        setPosition(newPos);
        positionRef.current = newPos;

        if (onMove)
            onMove(normalizePosition(newPos));
    }, [setIsDragging, setActiveTouchId, setPosition, calculateKnobPosition, normalizePosition, onMove]);

    const handleMove = useCallback((e) => {
        if (!isDragging) return;
        e.preventDefault();

        let touch = null;
        if (e.touches) {
            touch = [...e.touches].find(t => t.identifier === activeTouchId);
            if (!touch) return; // Ignore move events from other touches
        }

        // Get touch or mouse position
        const clientX = touch ? touch.clientX : e.clientX;
        const clientY = touch ? touch.clientY : e.clientY;

        const newPos = calculateKnobPosition(clientX, clientY);
        setPosition(newPos);
        positionRef.current = newPos;

        if (onMove)
            onMove(normalizePosition(newPos));
    }, [isDragging, activeTouchId, calculateKnobPosition, normalizePosition, onMove, setPosition]);

    const handleEnd = useCallback((e) => {
        if (!isDragging)
            return;
        e.preventDefault();

        if (e.touches && [...e.touches].some(t => t.identifier === activeTouchId)) {
            return; // Don't reset if the joystick's touch is still active
        }

        setIsDragging(false);
        setActiveTouchId(null); // Reset touch ID
        setPosition({ x: 0, y: 0 });
        positionRef.current = { x: 0, y: 0 };

        if (onStop)
            onStop();
    }, [isDragging, activeTouchId, setIsDragging, setActiveTouchId, setPosition, onStop]);
    let buttonDescription = "You gain access to move the fish once you successfully manage to parasitise it.";



    const knobStyle = {
        left: `calc(50% - 30px + ${position.x}px)`,
        top: `calc(50% - 30px + ${position.y}px)`
    };

    //Display Info Mode
    const [isButtonActive, setIsButtonActive] = useState(false);
    const handleButtonPress = useCallback(() => {
        setIsButtonActive(true);
    }, [setIsButtonActive]);

    const handleButtonRelease = useCallback(() => {
        setIsButtonActive(false);
    }, [setIsButtonActive]);

    useEffect(() => {
        if (!isInfoMode)
            return;
        if (isDisabled)
            return;
        
        const infoBtnElement = infoBtnRef.current;
        if (!infoBtnElement)
            return;

        const handleTouchStart = (event) => {
            if (isDisabled)
                return null;
            event.preventDefault(); // Prevent scroll/zoom on touch
            handleButtonPress();
            if (onClick) onClick(buttonDescription);
        };
        const handleTouchEnd = () => {
            handleButtonRelease();
            if (onClick) onClick();
        };

        const handler = {
            touchstart: handleTouchStart,
            touchend: handleTouchEnd,
            touchcancel: handleTouchEnd // Use same handler for cancel
        };

        infoBtnElement.addEventListener('touchstart', handler.touchstart, { passive: false });
        infoBtnElement.addEventListener('touchend', handler.touchend);
        infoBtnElement.addEventListener('touchcancel', handler.touchcancel);
        // --- Cleanup Function ---
        return () => {
            if (infoBtnElement) {
                // Remove the specific listeners that were added
                infoBtnElement.removeEventListener('touchstart', handler.touchstart, { passive: false });
                infoBtnElement.removeEventListener('touchend', handler.touchend);
                infoBtnElement.removeEventListener('touchcancel', handler.touchcancel);
            }
        }
    }, [isDisabled, isInfoMode, infoBtnRef, buttonDescription, onClick, handleButtonPress, handleButtonRelease]);

    useEffect(() => {
        if (isDisabled)
            return;
        if (isInfoMode)
            return;

        const container = containerRef.current;
        if (!container)
            return;

        // Define handlers within useEffect to avoid closure issues
        const touchStartHandler = (e) => handleStart(e);
        const touchMoveHandler = (e) => handleMove(e);
        const touchEndHandler = (e) => handleEnd(e);
        const mouseDownHandler = (e) => handleStart(e);
        const mouseMoveHandler = (e) => handleMove(e);
        const mouseUpHandler = (e) => handleEnd(e);

        container.addEventListener('touchstart', touchStartHandler, { passive: false });
        window.addEventListener('touchmove', touchMoveHandler, { passive: false });
        window.addEventListener('touchend', touchEndHandler, { passive: false });
        container.addEventListener('mousedown', mouseDownHandler);
        window.addEventListener('mousemove', mouseMoveHandler);
        window.addEventListener('mouseup', mouseUpHandler);

        return () => {
            container.removeEventListener('touchstart', touchStartHandler);
            window.removeEventListener('touchmove', touchMoveHandler);
            window.removeEventListener('touchend', touchEndHandler);
            container.removeEventListener('mousedown', mouseDownHandler);
            window.removeEventListener('mousemove', mouseMoveHandler);
            window.removeEventListener('mouseup', mouseUpHandler);
        };
    }, [isDisabled, isDragging, isInfoMode, handleEnd, handleMove, handleStart]);
    return (
        <div>
            {isInfoMode ? (
                <button
                    ref={infoBtnRef}
                    className={`joystick-container info ${isButtonActive ? 'active' : ''}`}
                    onMouseDown={(e) => {
                        if (e.button === 0) {
                            handleButtonPress();
                            if (onClick) onClick(buttonDescription);
                        }
                    }}
                    onMouseUp={(e) => {
                        if (e.button === 0)
                            handleButtonPress();
                    }}
                    onMouseLeave={() => {
                        handleButtonRelease();
                        if (onClick) onClick();
                    }}
                >
                    Joystick
                    {isButtonActive && (<div className="absolute -inset-2 bg-white opacity-20 rounded-full animate-ping pointer-events-none" />)}
                </button>
            ) : (
                <div className={isDisabled ? "joystick-container disabled" : "joystick-container"} ref={containerRef}>
                    <div
                        className={isDisabled ? "joystick-knob disabled" : `joystick-knob ${isDragging ? 'active' : ''}`}
                        ref={knobRef}
                        style={knobStyle}
                    />
                </div>)}
        </div>
    );
};

export default Joystick;